// Formato de los rankings para mostrar. ESM puro, sin imports de Next ni JSON:
// lo usan las vistas, el schema y next.config.mjs (llms.txt).
import { LOCAL_NICHE_LABELS } from './rankings-countries.js';

// Tipo de set de una medición: 'nacional' (set = el país), 'regiones' (spec 016) o 'sustentabilidad'
// (spec 017). El set lo fija rankings-data.mjs según el niche_type del informe.
export const setKind = (r) => (r.set === r.country ? 'nacional' : r.set.slice(r.country.length + 1));
export const SET_KINDS = ['nacional', 'regiones', 'sustentabilidad'];

// Período: el mes de las capturas ("septiembre de 2026"). Se mide una vez al mes; la semana ISO
// del JSON (ej. 2026-W39) es solo una referencia interna.
export const periodLabel = (r) =>
  new Date(r.captures_to).toLocaleDateString('es-CL', { month: 'long', year: 'numeric', timeZone: 'UTC' });

// Versión corta para cifras, etiquetas y tarjetas: "Sep 2026" (mes de 3 letras, como el resto del sitio).
const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
export const periodShort = (r) => {
  const d = new Date(r.captures_to);
  return `${MESES[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

// Solo el mes, para comparar con la medición anterior: "septiembre" (frases) y "Sep" (encabezados).
export const monthLabel = (r) => new Date(r.captures_to).toLocaleDateString('es-CL', { month: 'long', timeZone: 'UTC' });
export const monthShort = (r) => MESES[new Date(r.captures_to).getUTCMonth()];

// Nombre corto de cada industria para mostrar (cabe en una línea en las tarjetas del hub). Los datos
// traen nombres largos ("AFP / pensiones / previsión"); si una industria no está aquí, se usa el largo
// con comas en vez de barras.
const NICHE_SHORT = {
  'software-rrhh-remuneraciones': 'Software de RRHH y remuneraciones',
  'software-erp-gestion': 'Software ERP',
  'software-contabilidad-facturacion': 'Software contable y facturación',
  'software-crm-ventas': 'Software CRM',
  'plataformas-ecommerce-empresas': 'Plataformas de ecommerce',
  'pagos-pasarelas-pos-empresas': 'Pagos, pasarelas y POS',
  'software-logistica-ruteo': 'Software de logística y ruteo',
  'software-legal-contratos': 'Software legal y contratos',
  'plataformas-gestion-envios': 'Gestión de envíos',
  'portales-empleo-empresas': 'Portales de empleo para empresas',
  'estudios-juridicos-empresas': 'Estudios jurídicos para empresas',
  'asesoria-tributaria-empresas': 'Asesoría tributaria',
  'auditoria-contable-financiera': 'Auditoría contable y financiera',
  'compliance-riesgos-corporativos': 'Compliance y riesgos',
  'reclutamiento-seleccion-personal': 'Reclutamiento y selección',
  'headhunting-busqueda-ejecutiva': 'Búsqueda de ejecutivos',
  'staffing-servicios-temporales': 'Staffing y servicios temporales',
  'courier-paqueteria-empresas': 'Courier y paquetería para empresas',
  'courier-internacional-empresas': 'Courier internacional',
  'logistica-ultima-milla-ecommerce': 'Logística de última milla',
  'logistica-almacenaje-fulfillment': 'Almacenaje y fulfillment',
  'agencias-creativas-branding': 'Agencias creativas y branding',
  'agencias-performance-publicidad-digital': 'Agencias de publicidad digital',
  'agencias-seo-contenidos': 'Agencias de SEO y contenidos',
  'agencias-medios-planificacion-compra': 'Agencias de medios',
  'celulares-smartphones': 'Celulares nuevos',
  'computadores-notebooks': 'Computadores portátiles nuevos',
  'clinicas-y-centros-medicos': 'Clínicas y centros médicos',
  'educacion-online-y-cursos': 'Cursos online',
  'remesas-y-envio-de-dinero': 'Envío de dinero al extranjero',
  'seguridad-y-alarmas': 'Seguridad y alarmas',
  universidades: 'Universidades',
  preuniversitarios: 'Preparación de ingreso',
  'institutos-profesionales': 'Formación profesional superior',
  'centros-de-formacion-tecnica': 'Centros de formación técnica',
  'aerolineas-nacionales': 'Aerolíneas',
  'afp-pensiones-prevision': 'AFP',
  'agencias-de-marketing-publicidad-growth': 'Agencias de marketing',
  'alimento-para-mascotas': 'Alimento para mascotas',
  'arriendo-de-autos': 'Arriendo de autos',
  'automotriz-concesionarios-movilidad': 'Automotriz',
  bancos: 'Bancos',
  'buses-interurbanos': 'Buses interurbanos',
  'cajas-de-compensacion': 'Cajas de compensación',
  'calzado-deportivo-zapatillas': 'Zapatillas',
  cervezas: 'Cervezas',
  colchones: 'Colchones',
  'comida-rapida': 'Comida rápida',
  'delivery-apps-de-pedidos-last-mile': 'Delivery',
  'dermocosmetica-belleza': 'Dermocosmética',
  'educacion-universidades-preuniversitarios': 'Educación',
  'electrodomesticos-y-televisores': 'Electrodomésticos',
  'energia-solar-renovable-paneles': 'Energía solar',
  farmacias: 'Farmacias',
  'finanzas-seguros-fintech': 'Finanzas y fintech',
  'gimnasios-fitness': 'Gimnasios',
  'inmobiliarias-constructoras': 'Inmobiliarias',
  'inmobiliario-portales-propiedades': 'Inmobiliario',
  'institutos-tecnicos-cft': 'Institutos técnicos',
  'isapres-seguros-de-salud': 'Isapres',
  'legal-compliance-tributario-b2b': 'Legal y tributario',
  'logistica-courier-envios': 'Courier',
  'marcas-de-autos': 'Marcas de autos',
  'marketplaces-compras-online': 'Marketplaces',
  'mascotas-pet-shops': 'Mascotas',
  'medios-canales-de-tv': 'Canales de TV',
  'medios-diarios-prensa-escrita': 'Diarios',
  'medios-digitales-noticias': 'Noticias digitales',
  'medios-radios': 'Radios',
  'mejoramiento-del-hogar-ferreterias': 'Ferreterías',
  'muebles-decoracion': 'Muebles y decoración',
  opticas: 'Ópticas',
  pinturas: 'Pinturas',
  'retail-especializado-ticket-medio-alto': 'Retail especializado',
  'rrhh-reclutamiento-staffing': 'Reclutamiento',
  'saas-b2b-software-empresas': 'Software B2B',
  'seguros-generales-auto-hogar': 'Seguros generales',
  supermercados: 'Supermercados',
  'tecnologia-reacondicionada': 'Tecnología reacondicionada',
  'telecomunicaciones-telefonia-internet': 'Telecomunicaciones',
  'tiendas-por-departamento': 'Tiendas por departamento',
  'turismo-hoteles-agencias-experiencias': 'Turismo y hoteles',
  vinos: 'Vinos',
  // Industrias regionales de Chile (spec 016): el nombre lleva el lugar, porque se muestra solo en tarjetas,
  // títulos y llms.txt.
  'arriendo-camionetas-mineras': 'Camionetas para faena en Antofagasta',
  'astroturismo-coquimbo': 'Astroturismo en Coquimbo',
  'capacitacion-minera-otec': 'Capacitación minera en Antofagasta',
  'centros-de-ski': 'Centros de ski',
  'cerveza-artesanal-sur': 'Cerveza artesanal del sur',
  'cruceros-patagonia-antartica': 'Cruceros por Patagonia y Antártica',
  'enoturismo-casablanca': 'Enoturismo en Casablanca',
  'enoturismo-colchagua': 'Enoturismo en Colchagua',
  'pesca-deportiva-aysen': 'Pesca deportiva en Aysén',
  'pisco-elqui-limari': 'Pisco del Elqui y Limarí',
  'proveedores-acuicultura': 'Proveedores de acuicultura',
  'proveedores-mineros-antofagasta': 'Proveedores mineros en Antofagasta',
  'quesos-lacteos-sur': 'Quesos y lácteos del sur',
  'termas-del-sur': 'Termas del sur',
  'transporte-personal-minero': 'Transporte de personal minero',
  'turismo-arica-altiplano': 'Turismo en Arica y el altiplano',
  'turismo-aventura-pucon': 'Turismo aventura en Pucón',
  'turismo-chiloe': 'Turismo en Chiloé',
  'turismo-rapa-nui': 'Turismo en Rapa Nui',
  'turismo-san-pedro-atacama': 'Turismo en San Pedro de Atacama',
  'turismo-torres-del-paine': 'Turismo en Torres del Paine',
  'zofri-compras-iquique': 'Compras en la Zofri',
  // Ranking de sustentabilidad en IA (spec 017).
  'sustentabilidad-agroexportacion': 'Agroexportación',
  'sustentabilidad-alimentos-bebidas': 'Alimentos y bebidas',
  'sustentabilidad-banca': 'Banca',
  'sustentabilidad-construccion-cemento': 'Construcción y cemento',
  'sustentabilidad-empresas-chile': 'Grandes empresas de Chile',
  'sustentabilidad-energia-electrica': 'Generación eléctrica',
  'sustentabilidad-forestal-celulosa': 'Forestal y celulosa',
  'sustentabilidad-mineria': 'Minería',
  'sustentabilidad-pesca-industrial': 'Pesca industrial',
  'sustentabilidad-retail': 'Retail',
  'sustentabilidad-salmonicultura': 'Salmonicultura',
  'sustentabilidad-sanitarias': 'Sanitarias',
  'sustentabilidad-vitivinicola': 'Viñas',
};
export const nicheLabel = (r) => LOCAL_NICHE_LABELS[r.country]?.[r.niche_slug] ?? NICHE_SHORT[r.niche_slug] ?? r.niche_name.split(' / ').join(', ');

// "Chile - Nacional" → "Chile · Nacional".
export const geographyLabel = (geo) => geo.split(' - ').join(' · ');

// Título de un ranking: «Ranking IA: Bancos · Chile · Nacional». Una regional ya lleva el lugar en su
// nombre corto, así que va con el país: «Ranking IA: Turismo en Rapa Nui · Chile». Una de sustentabilidad
// lleva el nombre de su ranking: «Ranking de sustentabilidad en IA: Minería · Chile».
export const rankingTitle = (r) =>
  r.niche_type === 'sustentabilidad'
    ? `${SUSTAINABILITY_NAME}: ${nicheLabel(r)} · ${geographyLabel(r.geography)}`
    : `Ranking IA: ${nicheLabel(r)} · ${r.niche_type === 'regional-industria' ? r.geography.split(' - ')[0] : geographyLabel(r.geography)}`;

// Descripción de un ranking (metadata y schema).
export const rankingDescription = (r) =>
  r.niche_type === 'sustentabilidad'
    ? `A qué marcas presentan ChatGPT, Claude y Gemini como referentes en sustentabilidad en ${nicheLabel(r)}, ${periodLabel(r)}. Percepción en IA medida por Bison.`
    : `Ranking de visibilidad de marcas en ${nicheLabel(r)}, ${geographyLabel(r.geography)}, dentro de ChatGPT, Claude y Gemini, ${periodLabel(r)}. Medido por Bison.`;

// Metadata (<title> y description): se escalonan hasta caber, porque las industrias tienen nombres largos.
// Título ≤ 55 caracteres (70 con « | Bison Agents») y description ≤ 160, igual que los artículos (INI-1071).
// El h1, las tarjetas y el schema siguen con `rankingTitle` y `rankingDescription`.
export const RANKING_META_TITLE_MAX = 55;
export const RANKING_META_DESCRIPTION_MAX = 160;
// Industrias cuyo nombre largo (de `niche_name`) no cabe en un título: nombre corto solo para la meta.
const META_NICHE = { 'institutos-profesionales': 'Institutos profesionales' };

export function rankingMetaTitle(r) {
  const niche = META_NICHE[r.niche_slug] ?? nicheLabel(r);
  const lugar = geographyLabel(r.geography);
  const pais = r.geography.split(' - ')[0];
  const prefijos = r.niche_type === 'sustentabilidad' ? [SUSTAINABILITY_NAME, 'Sustentabilidad en IA'] : ['Ranking IA'];
  // Si el nombre de la industria ya dice el país («Grandes empresas de Chile»), no se repite.
  const lugares = niche.includes(pais) ? [''] : [` · ${lugar}`, ` · ${pais}`];
  const candidatos = prefijos.flatMap((p) => lugares.map((l) => `${p}: ${niche}${l}`));
  return candidatos.find((t) => t.length <= RANKING_META_TITLE_MAX) ?? `${niche} · ${pais}`;
}

export function rankingMetaDescription(r) {
  const niche = nicheLabel(r);
  const lugar = geographyLabel(r.geography);
  const candidatos = r.niche_type === 'sustentabilidad'
    ? [
        `A qué marcas presentan ChatGPT, Claude y Gemini como referentes en sustentabilidad en ${niche}, ${periodLabel(r)}. Percepción en IA medida por Bison.`,
        `A qué marcas presentan ChatGPT, Claude y Gemini como referentes en sustentabilidad en ${niche}, ${periodLabel(r)}.`,
      ]
    : [
        `Ranking de visibilidad de marcas en ${niche}, ${lugar}, dentro de ChatGPT, Claude y Gemini, ${periodLabel(r)}. Medido por Bison.`,
        `Ranking de visibilidad de marcas en ${niche}, ${lugar}, en ChatGPT, Claude y Gemini, ${periodLabel(r)}. Medido por Bison.`,
        `Ranking de visibilidad de marcas en ${niche}, ${lugar}, en ChatGPT, Claude y Gemini, ${periodLabel(r)}.`,
      ];
  return candidatos.find((d) => d.length <= RANKING_META_DESCRIPTION_MAX) ?? candidatos.at(-1);
}

// ── Ranking de sustentabilidad en IA (spec 017) ──
// Nombre público del ranking (decidido el 2026-10-26).
export const SUSTAINABILITY_NAME = 'Ranking de sustentabilidad en IA';
// La industria transversal (preguntas sin industria): el hub la destaca y su texto habla de «las empresas».
export const SUSTAINABILITY_TRANSVERSAL = 'sustentabilidad-empresas-chile';
// Pilares de las consultas (campo `pillar` del JSON), en el orden en que se muestran.
export const PILLARS = {
  clima: 'Clima y energía',
  agua: 'Agua',
  ecosistemas: 'Ecosistemas y residuos',
  comunidades: 'Comunidades y territorio',
  personas: 'Personas y trabajo',
  gobernanza: 'Gobernanza y transparencia',
};
export const PILLAR_ORDER = Object.keys(PILLARS);

// Métrica que ordena un ranking y cómo se nombra: visibilidad, o menciones favorables en sustentabilidad.
const METRICS = {
  visibility: { key: 'visibility_pct', top: 'top_visibility_pct', label: 'de visibilidad', short: 'visibilidad' },
  favorable: { key: 'favorable_pct', top: 'top_favorable_pct', label: 'de menciones favorables', short: 'favorables' },
};
export const rankMetric = (r) => (r.niche_type === 'sustentabilidad' ? METRICS.favorable : METRICS.visibility);

// Tipo de consulta (campo `intent` del JSON).
export const INTENTS = {
  general: 'General',
  comparative: 'Comparativa',
  'use-case': 'Caso de uso',
  regional: 'Regional',
  'price-value': 'Precio y valor',
  'purchase-decision': 'Decisión de compra',
};

// Consulta para mostrar. Los JSON traen las consultas como las escribe una persona en el chat
// (minúsculas, sin signos); en pantalla van con mayúscula inicial, nombres propios y signos de
// pregunta. Solo se corrige ortografía y puntuación: las palabras quedan intactas.
const PROPER = { afp: 'AFP', chile: 'Chile', concepción: 'Concepción', santiago: 'Santiago', antofagasta: 'Antofagasta', iphone: 'iPhone', macbook: 'MacBook' };
// Empieza con interrogativo → pregunta entera; interrogativo tras coma → pregunta desde ahí.
const ASK_START = /^(?:qué|cuál|cuáles|cuánto|cuánta|cuántos|cuántas|dónde|quién|cómo|en qué)(?!\p{L})/iu;
const ASK_AFTER_COMMA = /, ((?:qué|cuál|cuáles|cuánto|dónde|quién|cómo|en qué|igual)(?!\p{L}).*)$/iu;
// Las que necesitan una coma que el texto original no trae.
const PUNCTUATED = {
  'entre las afp que existen cuál cobra menos y al mismo tiempo da buena rentabilidad para mi fondo':
    'Entre las AFP que existen, ¿cuál cobra menos y al mismo tiempo da buena rentabilidad para mi fondo?',
  'quiero empezar a invertir tengo poco dinero qué opciones hay':
    'Quiero empezar a invertir, tengo poco dinero, ¿qué opciones hay?',
  // Industrias de octubre de 2026.
  'oye qué empresa de buses me recomiendas para viajar de noche por Chile, que sea segura, puntual y que no te cobren un ojo de la cara por el pasaje':
    'Oye, ¿qué empresa de buses me recomiendas para viajar de noche por Chile, que sea segura, puntual y que no te cobren un ojo de la cara por el pasaje?',
  'conviene más semicama o cama en viajes largos': '¿Conviene más semicama o cama en viajes largos?',
  'soy freelancer y necesito un auto para trabajar dónde lo compro': 'Soy freelancer y necesito un auto para trabajar, ¿dónde lo compro?',
  'necesito un auto usado con buen estado dónde busco en Chile': 'Necesito un auto usado con buen estado, ¿dónde busco en Chile?',
  'qué pet shop tiene el alimento de gato más barato, el saco de 10 kilos sale carísimo en todos lados':
    '¿Qué pet shop tiene el alimento de gato más barato? El saco de 10 kilos sale carísimo en todos lados.',
  'conviene más comprar la comida del perro en una tienda de mascotas online o en una tienda física, cuál tiene mejor despacho y atención':
    '¿Conviene más comprar la comida del perro en una tienda de mascotas online o en una tienda física? ¿Cuál tiene mejor despacho y atención?',
  'trabajo todo el día y no alcanzo a ir a comprar, hay alguna tienda de mascotas que tenga suscripción para que me llegue la comida del perro todos los meses a la casa':
    'Trabajo todo el día y no alcanzo a ir a comprar, ¿hay alguna tienda de mascotas que tenga suscripción para que me llegue la comida del perro todos los meses a la casa?',
  'es más barato comprar en un mayorista o en el súper normal si somos cuatro en la casa':
    '¿Es más barato comprar en un mayorista o en el súper normal si somos cuatro en la casa?',
  'quiero comprarme un iphone reacondicionado pero me da miedo que me salga malo, dónde es confiable comprar en Chile y que tenga garantía':
    'Quiero comprarme un iPhone reacondicionado pero me da miedo que me salga malo, ¿dónde es confiable comprar en Chile y que tenga garantía?',
  'vale la pena comprar notebook reacondicionado?': '¿Vale la pena comprar notebook reacondicionado?',
  'es mejor comprar un iphone reacondicionado en una tienda online o en una tienda física acá en Chile, cuál sale más a cuenta':
    '¿Es mejor comprar un iPhone reacondicionado en una tienda online o en una tienda física acá en Chile? ¿Cuál sale más a cuenta?',
  'vivo en Concepción y quiero comprar un celu reacondicionado, hay alguna tienda acá o es mejor pedir online con despacho a regiones':
    'Vivo en Concepción y quiero comprar un celu reacondicionado, ¿hay alguna tienda acá o es mejor pedir online con despacho a regiones?',
  // Industrias nuevas de W43 (categorías y medios).
  'cuál banco tiene la tasa más baja para crédito hipotecario ahora, estoy viendo pa comprarme un depa y quiero cotizar bien':
    '¿Cuál banco tiene la tasa más baja para crédito hipotecario ahora? Estoy viendo pa comprarme un depa y quiero cotizar bien.',
  'conviene más comprar en las páginas chinas o en las multitiendas de acá, considerando lo que se demora el envío y si hay que pagar aduana':
    '¿Conviene más comprar en las páginas chinas o en las multitiendas de acá, considerando lo que se demora el envío y si hay que pagar aduana?',
  'cuál inmobiliaria tiene mejor postventa, porque me han contado puros cachos con las garantías y no quiero caer en una de esas':
    '¿Cuál inmobiliaria tiene mejor postventa? Porque me han contado puros cachos con las garantías y no quiero caer en una de esas.',
  'conviene más comprar en verde con una inmobiliaria grande o en entrega inmediata con una más chica, cuál sale mejor en calidad y precio en UF':
    '¿Conviene más comprar en verde con una inmobiliaria grande o en entrega inmediata con una más chica? ¿Cuál sale mejor en calidad y precio en UF?',
  'conviene más comprar muebles en tiendas por departamento o en tiendas especializadas en Chile, cuál sale más a cuenta':
    '¿Conviene más comprar muebles en tiendas por departamento o en tiendas especializadas en Chile? ¿Cuál sale más a cuenta?',
  'llegan los muebles comprados por internet a Temuco o Puerto Montt, qué tiendas despachan al sur sin cobrar un ojo de la cara':
    '¿Llegan los muebles comprados por internet a Temuco o Puerto Montt? ¿Qué tiendas despachan al sur sin cobrar un ojo de la cara?',
  'entre las pizzerías de cadena cuál sale más barata y trae harto queso, porque algunas son puro pan y salen carísimas':
    'Entre las pizzerías de cadena, ¿cuál sale más barata y trae harto queso? Porque algunas son puro pan y salen carísimas.',
  'qué pizza a domicilio llega más rápido en Concepción, la última vez pedí y se demoró más de una hora y llegó helada':
    '¿Qué pizza a domicilio llega más rápido en Concepción? La última vez pedí y se demoró más de una hora y llegó helada.',
  'vivo en Concepción y quiero comprar cremas para la cara buenas, hay alguna tienda o farmacia que tenga harto surtido o mejor pido online con despacho':
    'Vivo en Concepción y quiero comprar cremas para la cara buenas, ¿hay alguna tienda o farmacia que tenga harto surtido o mejor pido online con despacho?',
  'oye qué marca de pellet para perro es buena y no tan cara, porque en el súper hay demasiadas y no cacho cuál conviene':
    'Oye, ¿qué marca de pellet para perro es buena y no tan cara? Porque en el súper hay demasiadas y no cacho cuál conviene.',
  'pellet premium vs pellet de supermercado, vale la pena la diferencia de precio':
    'Pellet premium vs. pellet de supermercado, ¿vale la pena la diferencia de precio?',
  'cuál es el canal de tele abierta que más ve la gente en Chile hoy en día, o ya nadie ve tele?':
    '¿Cuál es el canal de tele abierta que más ve la gente en Chile hoy en día, o ya nadie ve tele?',
  'cuál noticiero chileno es más objetivo y menos sensacionalista, porque siento que todos muestran puros portonazos':
    '¿Cuál noticiero chileno es más objetivo y menos sensacionalista? Porque siento que todos muestran puros portonazos.',
  'quiero cachar bien qué se está votando en el Congreso, hay algún canal que transmita las sesiones en vivo?':
    'Quiero cachar bien qué se está votando en el Congreso, ¿hay algún canal que transmita las sesiones en vivo?',
  'vivo en Antofagasta y quiero ver noticias de la región, qué canal tiene noticiero regional o solo pasan cosas de Santiago?':
    'Vivo en Antofagasta y quiero ver noticias de la región, ¿qué canal tiene noticiero regional o solo pasan cosas de Santiago?',
  'qué diario chileno es más objetivo, porque siento que todos tiran pa algún lado político y quiero algo más neutro':
    '¿Qué diario chileno es más objetivo? Porque siento que todos tiran pa algún lado político y quiero algo más neutro.',
  'qué medio chileno es más objetivo, porque siento que todos tiran para algún lado político':
    '¿Qué medio chileno es más objetivo? Porque siento que todos tiran para algún lado político.',
  'entre las radios de noticias cuál es más neutral políticamente y cuál cubre mejor cuando hay un temblor fuerte o alguna emergencia':
    'Entre las radios de noticias, ¿cuál es más neutral políticamente y cuál cubre mejor cuando hay un temblor fuerte o alguna emergencia?',
  // Industrias regionales de Chile (spec 016).
  'quiero ver el eclipse o una lluvia de estrellas en Chile, adónde voy':
    'Quiero ver el eclipse o una lluvia de estrellas en Chile, ¿adónde voy?',
  'soy mujer y quiero entrar a la minería en el norte, hay cursos o becas para mí':
    'Soy mujer y quiero entrar a la minería en el norte, ¿hay cursos o becas para mí?',
  'vamos en familia con niños que nunca han esquiado, a qué centro de ski vamos':
    'Vamos en familia con niños que nunca han esquiado, ¿a qué centro de ski vamos?',
  'voy a Valdivia en febrero, hay alguna fiesta de la cerveza o tour cervecero':
    'Voy a Valdivia en febrero, ¿hay alguna fiesta de la cerveza o tour cervecero?',
  'quiero hacer un viaje a la Antártica una vez en la vida, por dónde empiezo':
    'Quiero hacer un viaje a la Antártica una vez en la vida, ¿por dónde empiezo?',
  'soy jefe de mantención de una minera mediana y necesito externalizar la mantención de la flota, a quién llamo':
    'Soy jefe de mantención de una minera mediana y necesito externalizar la mantención de la flota, ¿a quién llamo?',
  'queso chanco de qué marca es el más rico':
    'Queso chanco, ¿de qué marca es el más rico?',
  'quiero ir a termas en invierno con nieve, adónde':
    'Quiero ir a termas en invierno con nieve, ¿adónde?',
  'conviene contratar transporte de personal o arrendar buses con conductor para faena':
    '¿Conviene contratar transporte de personal o arrendar buses con conductor para faena?',
  'conviene más irse al norte grande o a San Pedro para ver altiplano':
    '¿Conviene más irse al norte grande o a San Pedro para ver altiplano?',
  'viajo sola a Arica, con qué agencia hago el tour al Parque Lauca segura':
    'Viajo sola a Arica, ¿con qué agencia hago el tour al Parque Lauca segura?',
  'nunca he hecho trekking en volcán, estoy en condiciones de subir el Villarrica':
    'Nunca he hecho trekking en volcán, ¿estoy en condiciones de subir el Villarrica?',
  'Chiloé en invierno o en verano, cuándo conviene ir':
    'Chiloé en invierno o en verano, ¿cuándo conviene ir?',
  'pingüinera de Puñihuil desde Ancud, con quién ir':
    'Pingüinera de Puñihuil desde Ancud, ¿con quién ir?',
  'Rapa Nui barato o de lujo, cuánta diferencia hay realmente':
    'Rapa Nui barato o de lujo, ¿cuánta diferencia hay realmente?',
  'tour a los géiseres del Tatio con qué agencia ir, hay mucha diferencia de precio':
    'Tour a los géiseres del Tatio, ¿con qué agencia ir? ¿Hay mucha diferencia de precio?',
  'viajes a la Patagonia chilena qué conviene':
    'Viajes a la Patagonia chilena, ¿qué conviene?',
  'hacer la W en Torres del Paine, refugios o camping':
    'Hacer la W en Torres del Paine, ¿refugios o camping?',
  'vale la pena viajar a Iquique solo para comprar':
    '¿Vale la pena viajar a Iquique solo para comprar?',
};

export function queryLabel(text, country = 'chile') {
  // Brasil conserva las palabras y los signos de las consultas originales en portugués.
  if (country === 'brasil') return text.replace(/^(\p{Ll})/u, (c) => c.toUpperCase());
  if (PUNCTUATED[text]) return PUNCTUATED[text];
  let t = text.replace(/[\p{L}]+/gu, (w) => PROPER[w.toLowerCase()] ?? w);
  if (ASK_START.test(t)) t = `¿${t}?`;
  else t = t.replace(ASK_AFTER_COMMA, ', ¿$1?');
  t = t.replace('Santiago centro', 'Santiago Centro');
  return t.replace(/^(¿?)(\p{Ll})/u, (_, q, c) => q + c.toUpperCase());
}

// Alias para mostrar marcas con nombres muy largos (en pantalla). El schema conserva el nombre completo.
const BRAND_SHORT = {
  'Preuniversitario Pedro de Valdivia': 'Pedro de Valdivia',
  // Universidades: la sigla con que se las conoce en Chile.
  'Universidad Adolfo Ibáñez': 'UAI',
  'Universidad Andrés Bello': 'UNAB',
  'Pontificia Universidad Católica de Valparaíso': 'PUCV',
  'Universidad Católica del Norte': 'UCN',
  'Universidad de Concepción': 'UdeC',
  'Universidad Técnica Federico Santa María': 'USM',
  'Universidad del Desarrollo': 'UDD',
  'Universidad Católica de la Santísima Concepción': 'UCSC',
};
// El país al final distingue la filial (Movistar Chile, Nike México). Se conserva si forma parte
// del nombre propio o de una construcción como Banco de Crédito del Perú o Banco do Brasil.
const PAIS_EN_LA_MARCA = new Set(['Banco de Chile', 'CNN Chile', 'Correos Chile', 'Pluss Chile', 'Banco de la Nación Argentina']);
/** Nombre de la marca para mostrar y para el schema: sin el país que agrega el informe. */
export const brandName = (name) =>
  PAIS_EN_LA_MARCA.has(name) || /\b(?:de|del|do|da)\s+(?:la\s+)?(?:Chile|Perú|Brasil|Argentina|Colombia|Ecuador|México|Uruguay)$/.test(name)
    ? name
    : name.replace(/\s+(Chile|Perú|Brasil|Argentina|Colombia|Ecuador|México|Uruguay)$/, '');
/** Nombre en pantalla: el alias corto si lo tiene; si no, brandName. */
export const brandLabel = (name) => BRAND_SHORT[name] ?? brandName(name);

// Lista en prosa: "A", "A y B", "A, B y C".
export const listaEs = (items) => (items.length > 1 ? `${items.slice(0, -1).join(', ')} y ${items.at(-1)}` : items[0] ?? '');
